'use client'

import type { Kanji } from '@kanjido/database'
import { useState } from 'react'

import { DrawCanvas } from '@/components/practice/draw-canvas'
import { TasterLead } from '@/features/landing/method/taster-lead'
import { Eyebrow } from '@/features/landing/shared/eyebrow'
import type { KanjiStrokeData } from '@/kanjivg/types'
import { DrawPrompt } from '@/learn/kanji/[frame]/_components/draw-prompt'
import { StrokeAffordance } from '@/learn/kanji/[frame]/_components/stroke-affordance'

export type TasterKanji = Pick<
    Kanji,
    | 'character'
    | 'meaning'
    | 'frameNumber'
    | 'strokeCount'
    | 'revealStages'
    | 'gradeSlice'
    | 'strokeHost'
>

// The visitor's own drawing square is the app's draw beat for 古 "old"
// (frame 16), the kanji the figure assembled two frames ago, composed the
// way the Learn frame composes it: prompt, stroke-order strip, canvas. The
// canvas, the grader and the verdict are the app's own; nothing is written.
// The canvas runs with `redraw` (cross-path spec §5.0.10, v1.0.3): the app's
// "Continue →" is a concede with nothing to advance to here, so a miss
// offers "Try again" and a success "Draw it again", and the ink is
// read-only once graded. Lives app-side because it composes the Learn
// frame's leaves (spec §12); the lead copy stays with the landing feature.
export function Taster({
    kanji,
    strokeData,
    gradeStrokeData,
}: {
    kanji: TasterKanji | null
    // Unsliced host data for the stroke strip (loadDisplayStrokeData).
    strokeData: KanjiStrokeData | null
    // Grading slice for the canvas (loadGradeableStrokeData).
    gradeStrokeData: KanjiStrokeData | null
}) {
    const [done, setDone] = useState(false)
    if (!kanji || !strokeData || !gradeStrokeData) return null
    return (
        <div className="mx-auto grid max-w-5xl items-center gap-10 px-5 pt-6 pb-20 md:grid-cols-[4fr_8fr] md:gap-12 md:px-10">
            <TasterLead done={done} />
            {/* The app's draw beat, framed. The label sits on the frame's
                top rule like a plate caption. */}
            <div
                className="relative w-full max-w-xl justify-self-center border border-rule bg-paper px-5 pt-10 pb-8 md:px-8"
                data-taster
            >
                <Eyebrow
                    as="span"
                    size="sm"
                    className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-paper px-3 whitespace-nowrap"
                >
                    Frame {Number(kanji.frameNumber)} · the draw beat
                </Eyebrow>
                <div className="flex flex-col items-center gap-8">
                    <div className="flex w-full max-w-md flex-col gap-5">
                        <DrawPrompt keyword={kanji.meaning} />
                        <StrokeAffordance
                            character={kanji.character}
                            strokeCount={kanji.strokeCount}
                            revealStages={kanji.revealStages}
                            gradeSlice={kanji.gradeSlice}
                            strokeData={strokeData}
                            strokeHost={kanji.strokeHost}
                            centered
                        />
                    </div>
                    <DrawCanvas
                        character={kanji.character}
                        dataKey="landing-taster"
                        keyword={kanji.meaning}
                        mode="lenient"
                        overrideBlocked
                        redraw
                        strokeData={gradeStrokeData}
                        // fires on an accepted drawing only: a miss is
                        // redrawn, never conceded
                        onResolve={() => setDone(true)}
                    />
                </div>
            </div>
        </div>
    )
}
